import React, { useEffect } from 'react'
import InputMask from 'react-input-mask'
import * as yup from 'yup'
import { Moment } from 'moment'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    LinearProgress,
    SelectChangeEvent,
} from '@mui/material'
import { requiredMobile, requiredString } from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import DatePicker from '../../../../components/muiComponents/datePicker'
import { courseUserApiSlice } from '../../../../features/course/courseUserSlice'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import { setGroups } from '../../../../features/group/groupSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { getGroupsBySubjectsAndLanguage } from '../../../../features/group/groupActions'
import {
    convertToMobile,
    formatDateToCalendarDate,
} from '../../../../utils/functions'
import { CourseUserState } from '../../../../features/types'

const styles = {
    boxHeight: {
        height: '100px',
    },
}

interface Props {
    refetchStudents?: () => void
}

const AddStudentDialog: React.FC<Props> = ({ refetchStudents }) => {
    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const [open, setOpen] = React.useState(false)
    const [createCourseUser, { isLoading: isCreating }] =
        courseUserApiSlice.useCreateCourseStudentMutation()
    const { data: courses = [], isFetching: isFetchingCourses } =
        courseApiSlice.useFetchAllCoursesQuery(undefined, { skip: !open })

    const { groups = [], loading: isLoadingGroups } = useAppSelector(
        (state) => state.group,
    )

    const student: CourseUserState = {
        firstName: '',
        lastName: '',
        mobile: '',
        courseSubjectIds: [],
        languageEnum: 'KZ',
        startDate: null,
        endDate: null,
        studentGroupId: '',
        studentGroupIds: [],
        courseId: '',
        address: ""
    }

    const validationSchema = yup.object().shape({
        firstName: requiredString({ formatMessage }),
        lastName: requiredString({ formatMessage }),
        mobile: requiredMobile({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: student,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async () => {
            handleCreateCourseUser()
        },
    })

    const handleClickOpen = () => {
        setOpen(true)
        formik.resetForm()
    }

    const handleClose = () => {
        setOpen(false)
    }

    const {
        data: activeCourseSubjects = [],
        isFetching: isFetchingCourseSubjects,
    } = courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(
        Number(formik.values.courseId),
        { skip: !formik.values.courseId },
    )

    const handleCreateCourseUser = async () => {
        try {
            await createCourseUser({
                ...formik.values,
                mobile: convertToMobile(formik.values.mobile),
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            dispatch(setGroups([]))
            formik.resetForm()
            refetchStudents()
            handleClose()
        } catch (error) {}
    }

    const handleCourseChange = (
        event: SelectChangeEvent<typeof formik.values.courseId>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('courseId', value)
        formik.setFieldValue('courseSubjectIds', [])
        formik.setFieldValue('studentGroupIds', [])
        dispatch(setGroups([]))
    }

    const handleSubjectChange = (
        event: SelectChangeEvent<typeof formik.values.courseSubjectIds>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('courseSubjectIds', value)
        formik.setFieldValue('studentGroupIds', [])
    }

    const handleLanguageChange = (
        event: SelectChangeEvent<typeof formik.values.languageEnum>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('languageEnum', value)
        formik.setFieldValue('studentGroupIds', [])
    }

    const handleGroupChange = (
        event: SelectChangeEvent<typeof formik.values.studentGroupIds>,
    ) => {
        const {
            target: { value },
        } = event
        console.log('group change', value)
        formik.setFieldValue('studentGroupIds', value)
    }

    const handleDateChange = (key: string, event: Moment) => {
        formik.setFieldValue(
            key,
            formatDateToCalendarDate(event.toDate(), '-', ['yyyy', 'mm', 'd']),
        )
    }

    useEffect(() => {
        if (
            formik.values.courseSubjectIds.length &&
            formik.values.languageEnum
        ) {
            dispatch(
                getGroupsBySubjectsAndLanguage({
                    subjectIds: formik.values.courseSubjectIds,
                    languageEnum: formik.values.languageEnum,
                }),
            )
        }
    }, [formik.values.courseSubjectIds, formik.values.languageEnum])

    return (
        <div>
            <Button
                variant={'contained'}
                onClick={handleClickOpen}
            >
                {formatMessage({ id: 'addStudent' })}
            </Button>

            <Dialog
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'addStudent' })}
                </DialogTitle>
                <DialogContent>
                    {isFetchingCourses ||
                    isFetchingCourseSubjects ||
                    isLoadingGroups ? (
                        <LinearProgress />
                    ) : null}
                    
                    <form onSubmit={formik.handleSubmit}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ width: '100%', mr: 4 }}>
                                <Box
                                    mb={2}
                                    sx={{ height: '100px' }}
                                >
                                    <H6>
                                        {formatMessage({ id: 'firstName' })}
                                    </H6>

                                    <CustomInput
                                        id="firstName"
                                        name="firstName"
                                        placeholder={formatMessage({
                                            id: 'firstName',
                                        })}
                                        fullWidth
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        helperText={formik.errors.firstName}
                                        error={Boolean(formik.errors.firstName)}
                                        margin="dense"
                                    />
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'lastName' })}</H6>

                                    <CustomInput
                                        id="lastName"
                                        name="lastName"
                                        placeholder={formatMessage({
                                            id: 'lastName',
                                        })}
                                        fullWidth
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.lastName)}
                                        helperText={formik.errors.lastName}
                                        margin="dense"
                                    />
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>
                                        {formatMessage({ id: 'enterPhone' })}
                                    </H6>

                                    <InputMask
                                        mask="+7 (999) 999-99-99"
                                        value={formik.values.mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <CustomInput
                                            id="mobile"
                                            name="mobile"
                                            placeholder={formatMessage({
                                                id: 'enterPhone',
                                            })}
                                            fullWidth
                                            helperText={formik.errors.mobile}
                                            error={Boolean(
                                                formik.errors.mobile,
                                            )}
                                            margin="dense"
                                        />
                                    </InputMask>
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'course' })}</H6>

                                    <SingleSelect
                                        id="courseId"
                                        name="courseId"
                                        placeholder={formatMessage({
                                            id: 'course',
                                        })}
                                        value={formik.values.courseId}
                                        menuItems={courses}
                                        handleChange={handleCourseChange}
                                    />
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>Адрес</H6>

                                    <CustomInput
                                        id="address"
                                        name="address"
                                        placeholder="Адрес"
                                        fullWidth
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        margin="dense"
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%' }}>
                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'subject' })}</H6>

                                    <MultipleSelect
                                        id="courseSubjectIds"
                                        name="courseSubjectIds"
                                        value={formik.values.courseSubjectIds}
                                        handleChange={handleSubjectChange}
                                        menuItems={activeCourseSubjects}
                                        multiple={true}
                                        placeholder={formatMessage({
                                            id: 'selectSubject',
                                        })}
                                    />
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'language' })}</H6>

                                    <SingleSelect
                                        id="languageEnum"
                                        name="languageEnum"
                                        placeholder={formatMessage({
                                            id: 'language',
                                        })}
                                        value={formik.values.languageEnum}
                                        handleChange={handleLanguageChange}
                                        menuItems={[
                                            { id: 'RU', name: 'RU' },
                                            { id: 'KZ', name: 'KZ' },
                                        ]}
                                    />
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>
                                        {formatMessage({ id: 'start&endTime' })}
                                    </H6>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            mt: 2,
                                        }}
                                    >
                                        <Box sx={{ mr: 2 }}>
                                            <DatePicker
                                                label={formatMessage({
                                                    id: 'startDate',
                                                })}
                                                value={formik.values.startDate}
                                                handleChange={(event) =>
                                                    handleDateChange(
                                                        'startDate',
                                                        event,
                                                    )
                                                }
                                            />
                                        </Box>

                                        <Box>
                                            <DatePicker
                                                label={formatMessage({
                                                    id: 'endDate',
                                                })}
                                                value={formik.values.endDate}
                                                handleChange={(event) =>
                                                    handleDateChange(
                                                        'endDate',
                                                        event,
                                                    )
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={styles.boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'group' })}</H6>

                                    <MultipleSelect
                                        id="studentGroupIds"
                                        name="studentGroupIds"
                                        value={formik.values.studentGroupIds}
                                        handleChange={handleGroupChange}
                                        multiple={true}
                                        menuItems={groups}
                                        placeholder={formatMessage({
                                            id: 'group',
                                        })}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddStudentDialog
